import { useCallback, useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { Box, Button, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { ConnectionMapping, RegistryApiError, lhRegistryApiRef } from './api';
import { Pill, TICKET } from './ui';

/**
 * OM Database Service ↔ domain·conn_id 매핑 관리. service 마다 conn_id 가 1:1 이라 중복 제거 없이 전부 보여준다.
 * conn_id 는 이미 있는 Airflow connection 을 가리킬 뿐이다 — 여기서 만들지 않고 있는지 확인하지도 않는다.
 */
export const ConnectionMappings = ({ domains, isAdmin, omToken, reload = 0 }: { domains: string[]; isAdmin: boolean; omToken: () => Promise<string | undefined>; reload?: number }) => {
  const api = useApi(lhRegistryApiRef);
  const [rows, setRows] = useState<ConnectionMapping[]>();
  const [error, setError] = useState<string>();
  const [edit, setEdit] = useState<ConnectionMapping>();
  const [domain, setDomain] = useState('');
  const [connId, setConnId] = useState('');
  const [ticketKey, setTicketKey] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setRows(await api.listConnectionMappings(await omToken()));
      setError(undefined);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [api, omToken]);
  // reload 는 부모가 올리는 카운터 — connection 등록 직후 새 service·매핑이 새로고침 없이 보이게 한다
  useEffect(() => { load(); }, [load, reload]);

  const open = (r: ConnectionMapping) => {
    setEdit(r); setDomain(r.domain ?? ''); setConnId(r.connId); setTicketKey(''); setReason(''); setError(undefined);
  };
  const connBad = !/^[A-Za-z0-9_.-]{1,250}$/.test(connId);
  const ticketBad = !!ticketKey && !TICKET.test(ticketKey);
  const unchanged = !!edit && domain === (edit.domain ?? '') && connId === edit.connId;
  const canSave = isAdmin && !busy && !!edit && !connBad && TICKET.test(ticketKey) && !!reason.trim() && !unchanged;

  const save = async () => {
    if (!edit) return;
    setBusy(true);
    setError(undefined);
    try {
      await api.setConnectionMapping(edit.serviceName, { domain: domain || null, connId, version: edit.mapVersion, reason: reason.trim(), ticketKey }, await omToken());
      setEdit(undefined);
      await load();
    } catch (e) {
      const err = e as RegistryApiError;
      setError(err.code === 'VERSION_CONFLICT' ? '다른 사람이 먼저 저장했습니다. 목록을 다시 불러온 뒤 시도하세요' : err.message);
      if (err.code === 'VERSION_CONFLICT') await load();
    } finally {
      setBusy(false);
    }
  };

  const field = { variant: 'outlined' as const, size: 'small' as const };
  return (
    <Paper variant="outlined" component="section" aria-label="connection 매핑">
      <Box p={3} display="flex" flexDirection="column" style={{ gap: 12 }}>
        <Box>
          <Typography variant="h6" component="h2">connection 매핑</Typography>
          <Typography variant="body2" color="textSecondary">
            OM 서비스마다 domain 과 Airflow <code>conn_id</code> 를 하나씩 연결합니다. <code>conn_id</code> 는 이미 있는 Airflow connection 이름을 가리킵니다 —
            여기서 만들지 않습니다 (기본값 = <code>db_&lt;서비스 소문자&gt;</code>, om_connection_sync 가 만드는 이름).
          </Typography>
        </Box>
        {!isAdmin && <Alert severity="info"><b>읽기 전용</b> — 쓰기 권한이 없어 바꿀 수 없습니다.</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
        <fieldset disabled={!isAdmin} style={{ border: 0, margin: 0, padding: 0, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Table size="small" aria-label="connection 매핑 표">
            <TableHead>
              <TableRow>
                <TableCell>서비스</TableCell><TableCell>유형</TableCell><TableCell>domain</TableCell><TableCell>conn_id</TableCell><TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {(rows ?? []).map(r => (
                <TableRow key={r.serviceName} selected={edit?.serviceName === r.serviceName}>
                  <TableCell>{r.serviceName}</TableCell>
                  <TableCell>{r.serviceType}</TableCell>
                  <TableCell>{r.domain ?? <Typography variant="caption" color="textSecondary">미지정</Typography>}</TableCell>
                  <TableCell>
                    {r.connId} {r.connIdDefault && <Pill tone="neutral" title="저장된 매핑이 없어 기본값을 보여줍니다">기본</Pill>}
                    {r.conflictWith?.length > 0 && (
                      <Box mt={0.5}>
                        <Pill tone="warn" title="같은 conn_id 를 쓰는 서비스가 둘 이상이면 Airflow connection 이 섞입니다">
                          충돌: {r.conflictWith.join(', ')} 와(과) 같은 conn_id — 한쪽의 conn_id 를 편집(PUT)으로 바꿔 해소하세요
                        </Pill>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="right"><Button size="small" variant="outlined" onClick={() => open(r)} aria-label={`${r.serviceName} 편집`}>편집</Button></TableCell>
                </TableRow>
              ))}
              {rows?.length === 0 && <TableRow><TableCell colSpan={5}>OM 에 RDB 서비스가 없습니다</TableCell></TableRow>}
            </TableBody>
          </Table>
          {edit && (
            <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
              <Typography variant="subtitle2">{edit.serviceName} 매핑 변경</Typography>
              <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                <TextField id="cm-d" {...field} select SelectProps={{ native: true }} label="매핑 domain" value={domain} onChange={e => setDomain(e.target.value)} InputLabelProps={{ shrink: true }}>
                  <option value="">미지정</option>
                  {domains.map(d => <option key={d} value={d}>{d}</option>)}
                </TextField>
                <TextField id="cm-c" {...field} label="매핑 conn_id" value={connId} onChange={e => setConnId(e.target.value.trim())}
                  error={connBad} helperText={connBad ? '영문·숫자·_·.·- 만' : undefined} />
                <TextField id="cm-t" {...field} label="매핑 근거 티켓 (필수)" placeholder="DPD-13791" value={ticketKey} onChange={e => setTicketKey(e.target.value.trim())}
                  error={ticketBad} helperText={ticketBad ? '티켓 키 형식이 아닙니다' : undefined} />
                <TextField id="cm-r" {...field} label="매핑 사유 (필수)" value={reason} onChange={e => setReason(e.target.value)} />
              </Box>
              <Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
                <Button variant="outlined" onClick={() => setEdit(undefined)}>닫기</Button>
                <Button variant="contained" color="primary" onClick={save} disabled={!canSave}>매핑 저장</Button>
              </Box>
            </Box>
          )}
        </fieldset>
      </Box>
    </Paper>
  );
};
